// SuggestionThemeRow — Themen-Zeile der Vorschläge: „Deck-Themen"-Chips der genutzten Themen
// (Antippen schaltet stumm bzw. hebt auf), „+N" / „weniger anzeigen" nach der Zwei-Reihen-
// Grenze, Augen-Pille mit der Zahl der ausgeblendeten Karten und darunter die aufklappbare
// Zeile „Ausgeblendet". Ohne Profil steht stattdessen ein Hinweis.
import React, { useMemo, useRef, useState } from 'react';
import { EyeOff } from 'lucide-react';
import Badge from '../../../ui/Badge';
import CardPreview from '../components/CardPreview';
import { splitVisibleThemes, usedProfileThemes } from './profileThemes';
import { useTwoRowChipLimit } from './useTwoRowChipLimit';
import './SuggestionThemeRow.css';

/**
 * Fallback-Grenze für `useTwoRowChipLimit`, solange sich kein Layout messen lässt (vor dem
 * ersten Layout-Durchlauf, oder in jsdom ohne echtes Rendering). Vier Chips füllen auch bei
 * der minimalen Katalog-Spaltenbreite noch verlässlich zwei Reihen.
 */
const VISIBLE_PROFILE_TAGS = 4;

/**
 * @param {object[]} profile — Deck-Profil der Vorschlags-Antwort
 * @param {object[]} entries — die geladenen Vorschläge; ihre Begründungen bestimmen, welche
 *   Themen die Zeile zeigt
 * @param {{oracleId: string, name: string}[]} hiddenCards — ausgeblendete Karten des Decks
 * @param {(oracleId: string) => void} unhideCard — Karte wieder vorschlagen
 * @param {(tagId: string) => void} muteTag — Thema stummschalten
 * @param {(tagId: string) => void} unmuteTag — stummes Thema aufheben
 * @param {boolean} showProfile — Themen-Chips und Profil-Hinweis zeigen (die Vorschlags-Antwort
 *   ist geglückt und die Daten sind verfügbar). Die Augen-Pille hängt NICHT daran: sie liest
 *   die Einstellungen aus einer eigenen Abfrage, sonst käme man nach einem Fehlschlag der
 *   Liste an eine Ausblendung nicht mehr heran, die genau dafür verantwortlich sein kann,
 *   dass nichts mehr kommt.
 */
export default function SuggestionThemeRow({
  profile,
  entries,
  hiddenCards,
  unhideCard,
  muteTag,
  unmuteTag,
  showProfile,
}) {
  const [alleThemen, setAlleThemen] = useState(false);
  const [ausgeblendeteOffen, setAusgeblendeteOffen] = useState(false);
  const [kartenVorschau, setKartenVorschau] = useState(null);
  const messRef = useRef(null);

  // Die Zeile zeigt nur, was die Liste darunter gerade begründet (plus die stummen) —
  // das restliche Profil steht nirgends. „+N" klappt deshalb auch nur diese Menge auf
  // und bleibt eine Handvoll Chips, keine Wand aus mehreren hundert.
  const genutzteThemen = useMemo(() => usedProfileThemes(profile, entries), [profile, entries]);
  const zweiReihenGrenze = useTwoRowChipLimit(messRef, genutzteThemen.length + 1, VISIBLE_PROFILE_TAGS);
  const { visible: sichtbareThemen, hiddenCount: versteckteThemen } = useMemo(
    () => splitVisibleThemes(genutzteThemen, alleThemen ? Infinity : zweiReihenGrenze),
    [genutzteThemen, alleThemen, zweiReihenGrenze],
  );
  // Stumme Themen stehen als durchgestrichene Chips in der Themen-Zeile bzw. hinter dem
  // Aufklapp-Chip; eigenen Platz brauchen nur die ausgeblendeten Karten.
  const gibtAusgeblendete = hiddenCards.length > 0;
  const chipsSichtbar = showProfile && genutzteThemen.length > 0;

  const chip = (thema) => (
    <Badge
      key={thema.tagId}
      onClick={() => (thema.muted ? unmuteTag(thema.tagId) : muteTag(thema.tagId))}
      pressed={thema.muted}
      struck={thema.muted}
      title={thema.description || undefined}
    >
      {thema.label}
    </Badge>
  );

  // Augen-Pille am Zeilenende: zeigt die Zahl der ausgeblendeten Karten und klappt darunter
  // die Karten-Pillen auf. Klick auf eine Karten-Pille holt die Karte zurück in die Liste.
  const augenPille = gibtAusgeblendete ? (
    <Badge
      tone="accent"
      pressed={ausgeblendeteOffen}
      onClick={() => setAusgeblendeteOffen((offen) => !offen)}
      aria-label={`${hiddenCards.length} ausgeblendete Karten ${ausgeblendeteOffen ? 'verbergen' : 'anzeigen'}`}
      title="Ausgeblendete Karten"
    >
      <EyeOff size={12} aria-hidden />
      {hiddenCards.length}
    </Badge>
  ) : null;

  const vorschauGriffe = (karte) => ({
    onMouseEnter: (event) => setKartenVorschau({ karte, position: { x: event.clientX, y: event.clientY } }),
    onMouseMove: (event) => setKartenVorschau({ karte, position: { x: event.clientX, y: event.clientY } }),
    onMouseLeave: () => setKartenVorschau(null),
  });

  const ausgeblendeteZeile = gibtAusgeblendete && ausgeblendeteOffen ? (
    <div className="suggestion-theme-row__line suggestion-theme-row__hidden-cards" data-testid="ausgeblendete-karten">
      <span className="suggestion-theme-row__label">Ausgeblendet</span>
      {hiddenCards.map((karte) => (
        <Badge
          key={karte.oracleId}
          button
          struck
          title="Wieder vorschlagen"
          onClick={() => unhideCard(karte.oracleId)}
          {...vorschauGriffe(karte)}
        >
          {karte.name}
        </Badge>
      ))}
      <CardPreview card={kartenVorschau?.karte} position={kartenVorschau?.position} />
    </div>
  ) : null;

  if (chipsSichtbar || gibtAusgeblendete) {
    return (
      <div className="suggestion-theme-row">
        {chipsSichtbar && (
          // Mess-Kopie: alle Chips in der Zielbreite, unsichtbar — daraus liest der Hook die Grenze.
          // Dieselben Themen-Texte stehen dadurch doppelt im DOM (hier UND in der sichtbaren
          // Zeile darunter) — Tests dürfen nicht per Text suchen, sondern müssen die sichtbare
          // Zeile (`data-testid="deck-themen-zeile"`) bzw. ihre Buttons treffen.
          <div ref={messRef} className="suggestion-theme-row__line suggestion-theme-row__line--measure" aria-hidden>
            <span className="suggestion-theme-row__label">Deck-Themen</span>
            {genutzteThemen.map((thema) => (
              <span key={thema.tagId} className="badge">{thema.label}</span>
            ))}
            <span className="badge">+99</span>
            {gibtAusgeblendete && (
              // Die Augen-Pille zählt in der sichtbaren Zeile mit — ohne sie hier würde die
              // Messung einen Umbruch übersehen, den die Pille in der echten Zeile auslöst.
              <span className="badge">{`◉ ${hiddenCards.length}`}</span>
            )}
          </div>
        )}
        <div className="suggestion-theme-row__line" data-testid="deck-themen-zeile">
          {chipsSichtbar && (
            <>
              <span className="suggestion-theme-row__label">Deck-Themen</span>
              {sichtbareThemen.map(chip)}
              {versteckteThemen > 0 && (
                <Badge
                  onClick={() => setAlleThemen(true)}
                  aria-label={`Weitere ${versteckteThemen} Themen anzeigen`}
                >
                  {`+${versteckteThemen}`}
                </Badge>
              )}
              {alleThemen && zweiReihenGrenze !== Infinity && (
                <Badge onClick={() => setAlleThemen(false)}>weniger anzeigen</Badge>
              )}
            </>
          )}
          {augenPille}
        </div>
        {ausgeblendeteZeile}
      </div>
    );
  }

  if (showProfile && profile.length === 0) {
    return (
      <p className="suggestion-theme-row__hint">
        Füge ein paar Karten hinzu, dann erkennen wir dein Deck-Profil.
      </p>
    );
  }

  return null;
}
